import React, {RefObject} from 'react';
import {queryString} from 'utilities/url';
import {Helmet} from 'react-helmet';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {
  AppLayout,
  PageHeader,
  Breadcrumbs,
} from 'sections/services-marketplace/experts/components';
import {TaxonomyNode, BrowseExpertsFilters, Service} from 'sections/types';
import {compose} from 'react-apollo';
import {withRouter, RouteComponentProps} from 'react-router';
import withI18nArgs from './locales';
import isEmpty from 'lodash/isEmpty';
import {getServicesMarketplacePublicClient} from 'utilities/graphql/client';
import GetProfiles, {
  GetProfilesQueryData,
} from './graphql/GetProfilesQuery.graphql';
import {FilterInputs, FilterCollection, Results} from './components';
import {Routes} from 'sections/services-marketplace/experts/utilities/routes';
import {fetchTaxonomy} from 'sections/services-marketplace/experts/utilities';
import {producePageViewMonorailEvent} from '../../utilities/monorail';
import {MonorailPageIndicators} from '../../utilities/constants';
import {notifyException} from 'utilities/analytics/bugsnag';
import {Banner} from '@shopify/polaris';

export interface OwnProps {
  taxonomyNode: TaxonomyNode;
}

interface SearchMetadata {
  countries: string[];
}

type CombinedProps = OwnProps &
  WithI18nProps &
  RouteComponentProps<{
    serviceSlug: string;
  }>;

interface State {
  appliedFilters: BrowseExpertsFilters;
  localFilters: BrowseExpertsFilters;
  loading: boolean;
  profiles?: GetProfilesQueryData.Profiles;
  searchMetadata: SearchMetadata;
  page: number;
  error: boolean;
}

const DEFAULT_STATE = {
  appliedFilters: {},
  localFilters: {},
  loading: true,
  searchMetadata: {
    countries: [],
  },
  page: 0,
  error: false,
};

const client = getServicesMarketplacePublicClient();

class ExpertIndex extends React.PureComponent<CombinedProps, State> {
  state: State = DEFAULT_STATE;

  scrollRef: RefObject<HTMLDivElement> = React.createRef();

  taxonomy = fetchTaxonomy();

  onNextPage = () => {
    this.scrollToRef();
    this.setState(({page}) => ({page: page + 1}), this.search);
  };

  onPrevPage = () => {
    this.scrollToRef();
    this.setState(({page}) => ({page: page - 1}), this.search);
  };

  scrollToRef = () => {
    if (!this.scrollRef.current) return;
    this.scrollRef.current.scrollIntoView();
  };

  onPriceRangeChange = (key: string) => {
    const {localFilters} = this.state;

    return (value: string): void => {
      this.setState({
        localFilters: {
          ...localFilters,
          [key]: value,
        },
      });
    };
  };

  onPriceRangeReset = () => {
    const {localFilters} = this.state;

    this.setState(
      {
        localFilters: {
          ...localFilters,
          minPrice: undefined,
          maxPrice: undefined,
        },
      },
      this.onSubmitFilters
    );
  };

  onLocationSelect = (value: string) => {
    const {localFilters} = this.state;

    this.setState({
      localFilters: {
        ...localFilters,
        location: value,
      },
    });
  };

  onLocationRemove = () => {
    const {localFilters} = this.state;

    this.setState(
      {
        localFilters: {
          ...localFilters,
          location: undefined,
        },
      },
      this.onSubmitFilters
    );
  };

  onLanguageChange = (value: string[]): void => {
    const {localFilters} = this.state;

    this.setState({
      localFilters: {
        ...localFilters,
        languages: value,
      },
    });
  };

  onLanguageRemove = (language: string) => {
    const {
      localFilters,
      localFilters: {languages},
    } = this.state;

    return () => {
      const updatedLanguages = languages
        ? languages.filter(item => item !== language)
        : undefined;

      this.setState(
        {
          localFilters: {
            ...localFilters,
            languages: updatedLanguages,
          },
        },
        this.onSubmitFilters
      );
    };
  };

  generateStateFromQuery = () => {
    const {
      location: {search},
    } = this.props;

    const parsedQuery = queryString.parse(search);

    if (parsedQuery.languages) {
      parsedQuery.languages = [].concat(parsedQuery.languages);
    }

    return parsedQuery;
  };

  generateQueryFromState = () => {
    const {appliedFilters, page} = this.state;
    return queryString.stringify({
      ...appliedFilters,
      page: page > 0 ? page + 1 : undefined,
    });
  };

  applyFiltersFromQuery = (config = {updateHistory: false}) => {
    const filterState = this.generateStateFromQuery();

    this.setState(
      {
        appliedFilters: filterState,
        localFilters: filterState,
        page: Number(filterState.page - 1) || 0,
      },
      () => this.search({updateHistory: config.updateHistory})
    );
  };

  componentDidUpdate(prevProps: Readonly<CombinedProps>) {
    if (
      prevProps.taxonomyNode.service.handle !==
      this.props.taxonomyNode.service.handle
    ) {
      this.freshResults();
      producePageViewMonorailEvent({
        pageIndicator: MonorailPageIndicators.expertIndex,
        serviceHandle: this.props.taxonomyNode.service.handle,
        rankingPageNumber: this.state.page,
      });
    }
  }

  freshResults = () => {
    this.setState(DEFAULT_STATE, this.search);
  };

  onSubmitFilters = () => {
    const {appliedFilters, localFilters} = this.state;

    this.setState(
      {
        page: 0,
        appliedFilters: {
          ...appliedFilters,
          ...localFilters,
        },
      },
      this.search
    );
  };

  onResetFilters = () => {
    const {localFilters} = this.state;

    if (isEmpty(localFilters)) {
      return;
    }

    this.setState(DEFAULT_STATE, this.onSubmitFilters);
  };

  search = (config = {updateHistory: true}) => {
    const {history} = this.props;
    this.fetchProfiles();
    if (config.updateHistory) {
      history.push({search: this.generateQueryFromState()});
    }
  };

  reloadPage = () => {
    window.location.reload();
  };

  componentDidMount() {
    const {history} = this.props;

    this.applyFiltersFromQuery({updateHistory: false});

    this.unlisten = history.listen((_, action) => {
      if (action === 'POP') {
        this.applyFiltersFromQuery();
      }
    });

    producePageViewMonorailEvent({
      pageIndicator: MonorailPageIndicators.expertIndex,
      serviceHandle: this.props.taxonomyNode.service.handle,
      rankingPageNumber: this.state.page,
    });
  }

  async fetchProfiles() {
    this.setState({
      loading: true,
    });

    try {
      const {
        data: {profiles, searchMetadata},
      } = await client.query<{
        profiles: GetProfilesQueryData.Profiles;
        searchMetadata: GetProfilesQueryData.SearchMetadata;
      }>({
        query: GetProfiles,
        variables: {
          filters: {
            serviceHandle: this.props.taxonomyNode.service.handle,
            minPrice: this.state.appliedFilters.minPrice,
            maxPrice: this.state.appliedFilters.maxPrice,
            languages: this.state.appliedFilters.languages,
            location: this.state.appliedFilters.location,
          },
          after: this.state.page.toString(),
        },
      });
      this.setState({profiles, searchMetadata, loading: false});
    } catch (error) {
      this.setState({
        error: true,
      });
      notifyException({error});
    }
  }

  componentWillUnmount() {
    this.unlisten();
  }

  render() {
    const {
      taxonomyNode: {service},
      i18n,
    } = this.props;
    const {
      localFilters,
      appliedFilters,
      profiles,
      searchMetadata,
      loading,
      error,
    } = this.state;

    const locations = searchMetadata.countries.map((country: string) => ({
      value: country,
      label: country,
    }));

    const ancestors = this.taxonomy.getAncestors(service.handle);
    const parentCategory = ancestors.length ? ancestors[0] : service;

    return (
      <AppLayout>
        {this.renderHelmet(service)}
        <AppLayout.Container>
          <AppLayout.Section fill>
            <PageHeader
              title={service.label}
              description={service.description}
              image={Routes.imagePath(`svg/${parentCategory.handle}.svg`)}
              actions={<Breadcrumbs services={ancestors} />}
            />
          </AppLayout.Section>
        </AppLayout.Container>
        <AppLayout.Container>
          <div ref={this.scrollRef} />
          <AppLayout.Section size="OneFifth">
            <FilterInputs
              locations={locations}
              filterState={localFilters}
              filterChangeHandlers={{
                onPriceRangeChange: this.onPriceRangeChange,
                onLocationSelect: this.onLocationSelect,
                onLanguageChange: this.onLanguageChange,
                onSubmitFilters: this.onSubmitFilters,
                onResetFilters: this.onResetFilters,
              }}
            />
          </AppLayout.Section>
          <AppLayout.Section fill>
            <FilterCollection
              filterState={appliedFilters}
              filterChangeHandlers={{
                onPriceRangeReset: this.onPriceRangeReset,
                onLocationRemove: this.onLocationRemove,
                onLanguageRemove: this.onLanguageRemove,
                onResetFilters: this.onResetFilters,
              }}
            />
            {error ? (
              <Banner
                title={i18n.translate('ExpertIndex.ErrorState.bannerTitle')}
                action={{
                  content: i18n.translate('ExpertIndex.ErrorState.buttonLabel'),
                  onAction: this.reloadPage,
                }}
                status="critical"
              />
            ) : (
              <Results
                profiles={profiles}
                service={service}
                loading={loading}
                onResetFilters={this.onResetFilters}
                onNextPage={this.onNextPage}
                onPrevPage={this.onPrevPage}
              />
            )}
          </AppLayout.Section>
        </AppLayout.Container>
      </AppLayout>
    );
  }

  renderHelmet = (service: Service) => {
    const {i18n} = this.props;
    const canonicalPath = `https://experts.shopify.com${Routes.servicePath(
      this.taxonomy,
      service.handle
    )}`;

    return (
      <Helmet>
        <title>
          {i18n.translate('ExpertIndex.Meta.title', {
            serviceLabel: service.shortLabel.toLowerCase(),
          })}
        </title>
        <meta
          name="description"
          content={i18n.translate('ExpertIndex.Meta.description', {
            serviceLabel: service.shortLabel.toLowerCase(),
          })}
        />
        <link rel="canonical" href={canonicalPath} />
      </Helmet>
    );
  };

  private unlisten: Function = (): void => {};
}

export default compose(
  withRouter,
  withI18n(withI18nArgs)
)(ExpertIndex);
